import { nativeSetup } from '../platform/nativeSetup';
import network from '../platform/network';
import api from '../utils/axiosConfig';
import notifications from '../platform/notifications';
import {
    login,
    finishDeviceLogout,
    lockSession,
    logout,
    setActiveUnit,
    setAuthChecking,
    setFirstLogin,
    setSignedEula,
    setUser
} from '../store/authSlice';
import { biometrics, isBiometricLock } from '../platform/biometrics';
import { runtime } from '../platform/runtime';
import { authStorage } from '../platform/storage';
import { loadStoredSession, SESSION_STATUS } from '../platform/sessionState.mjs';
import { toast } from 'react-toastify';

let persistenceQueue = Promise.resolve();

/**
 * Authentication middleware - currently minimal, can be extended for other auth-related logic
 */
export const authMiddleware = store => next => action => {
    const previousUserId = store.getState().auth.user?.user;
    const result = next(action);
    const auth = store.getState().auth;
    const explicitLogout = logout.match(action) && action.payload?.explicitDeviceLogout === true;
    let pushCleanup = Promise.resolve();
    if (runtime.isNative()) {
        if (login.match(action)) {
            void notifications.setSession({ userId: auth.user?.user, token: auth.token }).catch(() => {});
        } else if (logout.match(action)) {
            pushCleanup = explicitLogout
                ? notifications.logout({ forgetPreference: true })
                : notifications.setSession(null);
            void pushCleanup.catch(() => {});
        } else if (lockSession.match(action)) {
            void notifications.setSession(null).catch(() => {});
        }
    }


    const persist = async () => {
        if (login.match(action)) {
            await Promise.all([
                authStorage.set('token', auth.token),
                authStorage.set('units', auth.units),
                authStorage.set('activeUnit', auth.activeUnit),
                authStorage.set('signedEula', auth.signedEula),
                authStorage.set('isFirstLogin', auth.isFirstLogin),
                authStorage.set('user', auth.user)
            ]);
        } else if (logout.match(action)) {
            if (runtime.isNative() && !explicitLogout) await biometrics.lock();
            else {
                if (runtime.isNative() && explicitLogout) await nativeSetup.reset(previousUserId);
                await authStorage.clearAuth();
                if (runtime.isNative() && explicitLogout) biometrics.credentialsCleared();
            }
        } else if (setActiveUnit.match(action)) {
            await authStorage.set('activeUnit', action.payload);
        } else if (setSignedEula.match(action)) {
            await authStorage.set('signedEula', action.payload.signedEula);
        } else if (setFirstLogin.match(action)) {
            await authStorage.set('isFirstLogin', action.payload);
        } else if (setUser.match(action)) {
            await authStorage.set('user', action.payload);
        }
    };

    const persisted = persistenceQueue.then(persist);
    persistenceQueue = persisted.catch((error) => {
        console.error('Unable to persist authentication state.');
        if (runtime.isNative() && !logout.match(action)) store.dispatch(isBiometricLock(error) ? lockSession() : logout());
    });
    // Explicit Logout waits for secure deletion and device push cleanup before
    // offering another login. Failures remain visible and retryable in the dialog.
    if (explicitLogout) return Promise.allSettled([persisted, pushCleanup]).then(results => {
        // Keep login unmounted until both cleanup operations settle, even if
        // one fails early. The header stays mounted to offer a visible retry.
        store.dispatch(finishDeviceLogout());
        const failure = results.find(result => result.status === 'rejected');
        if (failure) throw failure.reason;
    });

    return result;
};

/**
 * Initialize authentication state by validating stored token
 * Call this immediately after store creation
 */
export const initializeAuth = async (store, { showLoading = true } = {}) => {
    if (showLoading) store.dispatch(setAuthChecking(true));

    let session;
    try {
        if (runtime.isNative() && !(await network.getStatus()).connected) {
            store.dispatch(lockSession());
            return 'unavailable';
        }
        session = await loadStoredSession(authStorage);
    } catch (error) {
        if (isBiometricLock(error) || error.code === 'SESSION_REFRESH_UNAVAILABLE') {
            store.dispatch(lockSession());
            return error.code === 'SESSION_REFRESH_UNAVAILABLE' ? 'unavailable' : undefined;
        }
        try { await authStorage.clearAuth(); } catch { /* Remain signed out. */ }
        store.dispatch(logout());
        toast.error('Unable to restore your session. Please sign in again.');
        return;
    }

    if (session.status === SESSION_STATUS.VALID) {
        // Token exists, validate it
        return await validateTokenAndRehydrateState(store, session.token, session.activeUnit, showLoading);
    } else {
        if (session.status !== SESSION_STATUS.MISSING) {
            await authStorage.clearAuth();
        }
        store.dispatch(setAuthChecking(false));
    }
};

/**
 * Function to validate token and rehydrate state
 */
const validateTokenAndRehydrateState = async (store, token, activeUnit, showLoading) => {
    if (import.meta.env.MODE === 'development') {
        await new Promise(resolve => setTimeout(resolve, 800));
    }
    if (showLoading) store.dispatch(setAuthChecking(true));
    try {
        // Make a request to validate the token and get user data
        const response = await api.get('/api/auth/validate', {
            _nativeAuth: runtime.isNative(),
            headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.valid) {
            // A background lock or logout while validation was in flight must
            // not restore authenticated UI from the stale response.
            const validatedToken = response.config?.headers?.Authorization?.replace(/^Bearer /, '') || token;
            if (runtime.isNative() && await authStorage.get('token') !== validatedToken) {
                store.dispatch(lockSession());
                return;
            }
            // If token is valid, dispatch login action with user data
            store.dispatch(login({
                token: validatedToken,
                user: response.data.user,
                units: response.data.units || [],
                isFirstLogin: response.data.isFirstLogin,
                signedEula: response.data.signedEula,
                activeUnit,
            }));
        } else {
            store.dispatch(logout());
            toast.error('Session expired. Please log in again.');
        }
    } catch (error) {
        if (isBiometricLock(error) || error.code === 'SESSION_REFRESH_UNAVAILABLE') {
            store.dispatch(lockSession());
            return error.code === 'SESSION_REFRESH_UNAVAILABLE' ? 'unavailable' : undefined;
        }
        if (runtime.isNative() && (!error.response || error.response.status >= 500)) { store.dispatch(lockSession()); return 'unavailable'; }
        console.warn('Unable to validate session.');
        store.dispatch(logout());
        toast.error('Session expired. Please log in again.');
    } finally {
        // Always set auth checking to false when we're done
        store.dispatch(setAuthChecking(false));
    }
};
